/**
 * <InstallButtons /> — the "Add to Cursor / VS Code / Claude" row.
 *
 * Two layout flavors via `variant`:
 *
 *  - `default` — three primary buttons in a row, then a small "or copy
 *    the snippet" disclosure for Claude Desktop / Claude.ai. Used on the
 *    landing's <ForAgents /> section and the dedicated /install page.
 *  - `compact` — single-line, smaller buttons. Used inside the API keys
 *    settings card so existing users can self-serve a fresh client
 *    install without leaving the page.
 *
 * Auto-mint flow:
 *  1. User clicks "Add to Cursor".
 *  2. If signed in → POST /api/keys (named "Cursor — <date>") → bake
 *     plaintext into the deeplink → window.location.assign(cursor://…).
 *  3. We *also* surface the new key in a reveal card below the buttons
 *     because deeplinks fail silently when the protocol handler isn't
 *     registered (e.g. Cursor not installed) — the user can still copy
 *     the JSON snippet with the key pre-filled.
 *
 * Signed-out users see "Sign in to install" which redirects to /sign-up
 * with a `?return=/install` so they bounce back to a state that matters.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Check, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/useSession';
import { ApiError } from '@/lib/api';
import type { CreatedApiKey } from '@/lib/api-keys';
import {
  buildMcpConfigJson,
  installClient,
  type InstallClient,
} from '@/lib/install';
import { ClaudeLogo, CursorLogo, VSCodeLogo } from './ClientLogos';

export interface InstallButtonsProps {
  /** Visual flavor — see file header. */
  variant?: 'default' | 'compact';
  /** Append a class to the wrapper. */
  className?: string;
  /**
   * Where to send signed-out users when they click. Defaults to /sign-in
   * with a return URL — most install-button clickers already have an
   * account, the sign-in page itself offers the "Create account" link
   * for the rest.
   */
  signInRedirect?: string;
}

interface ClientButtonConfig {
  client: InstallClient;
  label: string;
  Logo: (props: React.SVGProps<SVGSVGElement>) => React.ReactElement;
}

const PRIMARY_CLIENTS: ClientButtonConfig[] = [
  { client: 'cursor', label: 'Cursor', Logo: CursorLogo },
  { client: 'vscode', label: 'VS Code', Logo: VSCodeLogo },
  { client: 'claude-desktop', label: 'Claude Desktop', Logo: ClaudeLogo },
];

export function InstallButtons({
  variant = 'default',
  className,
  signInRedirect = '/sign-in?return=/install',
}: InstallButtonsProps) {
  const { user, loading } = useSession();
  const [busy, setBusy] = useState<InstallClient | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{ client: InstallClient; key: CreatedApiKey } | null>(
    null,
  );

  const isCompact = variant === 'compact';

  async function handleClick(client: InstallClient) {
    if (!user) {
      // Send signed-out users to sign-up — once they're back, they can
      // re-click. We deliberately don't try to remember the *intent* across
      // the redirect (no `?action=cursor`); the install page auto-handles
      // that param on its own, and other surfaces just need the user
      // signed-in for the second click.
      window.location.assign(signInRedirect);
      return;
    }

    setError(null);
    setBusy(client);
    try {
      // Claude Desktop / Claude.ai have no deeplink — for the compact row
      // we just point at /install where the full setup card lives. The
      // default variant renders a separate snippet card below, so we
      // never hit this path with those clients there.
      if (client === 'claude-desktop' || client === 'claude-web') {
        window.location.assign('/install');
        return;
      }

      const result = await installClient(client, { autoMint: true }, user.id);
      if (result.created) {
        setRevealed({ client, key: result.created });
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Install failed.');
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div
        className={cn(
          'flex flex-wrap',
          isCompact ? 'gap-2' : 'gap-3',
        )}
      >
        {PRIMARY_CLIENTS.map(({ client, label, Logo }) => {
          const isBusy = busy === client;
          return (
            <Button
              key={client}
              type="button"
              size={isCompact ? 'sm' : 'default'}
              variant={client === 'cursor' ? 'default' : 'outline'}
              onClick={() => handleClick(client)}
              disabled={isBusy || loading}
              className={cn(isCompact ? 'h-8' : 'h-10')}
            >
              {isBusy ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Logo className="size-4" />
              )}
              {!user && !loading ? `Sign in to add to ${label}` : `Add to ${label}`}
            </Button>
          );
        })}

        {!isCompact ? (
          <Button asChild variant="ghost" size="default" className="h-10">
            <Link to="/install">
              All install options <ExternalLink className="size-3.5" />
            </Link>
          </Button>
        ) : null}
      </div>

      {error ? (
        <p className="text-destructive flex items-start gap-1.5 text-xs">
          <AlertCircle className="size-3.5 shrink-0 translate-y-[1px]" />
          <span className="leading-relaxed">{error}</span>
        </p>
      ) : null}

      {revealed ? (
        <RevealedKeyFallback
          client={revealed.client}
          createdKey={revealed.key.key}
          onDismiss={() => setRevealed(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * Shown right after a successful auto-mint. The deeplink already handed the
 * key to the host, but if the deeplink silently failed (no protocol
 * handler registered) the user is now stuck with a key that only exists
 * in this DOM tree — surface it so they can still finish the install
 * manually. Also gives them a paste-ready JSON snippet with the new key
 * baked in.
 */
function RevealedKeyFallback({
  client,
  createdKey,
  onDismiss,
}: {
  client: InstallClient;
  createdKey: string;
  onDismiss: () => void;
}) {
  const snippet = buildMcpConfigJson(createdKey);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedJson, setCopiedJson] = useState(false);

  function copyKey() {
    navigator.clipboard.writeText(createdKey).then(() => {
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 1500);
    });
  }
  function copyJson() {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopiedJson(true);
      setTimeout(() => setCopiedJson(false), 1500);
    });
  }

  const clientLabel =
    PRIMARY_CLIENTS.find((c) => c.client === client)?.label ?? client;

  return (
    <Card className="border-primary/30 bg-primary/[0.04] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">
            {clientLabel} should now be opening with SendFast pre-filled.
          </p>
          <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
            Nothing happened? {clientLabel} may not be installed, or the protocol handler is
            blocked — copy the snippet below and paste it into your config manually. Save the
            key somewhere safe; we won't show it again.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onDismiss} aria-label="Dismiss">
          Dismiss
        </Button>
      </div>

      <div className="bg-background border-border mt-3 flex items-center gap-2 rounded-md border p-3">
        <code className="min-w-0 flex-1 truncate font-mono text-xs">{createdKey}</code>
        <Button variant="outline" size="sm" onClick={copyKey}>
          {copiedKey ? (
            <>
              <Check className="size-3" /> Copied
            </>
          ) : (
            <>
              <Copy className="size-3" /> Copy key
            </>
          )}
        </Button>
      </div>

      <div className="bg-background border-border mt-3 overflow-hidden rounded-md border">
        <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
          <span className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
            mcp config
          </span>
          <Button variant="ghost" size="sm" onClick={copyJson} className="h-6 px-2 text-[10px]">
            {copiedJson ? (
              <>
                <Check className="size-3" /> Copied
              </>
            ) : (
              <>
                <Copy className="size-3" /> Copy
              </>
            )}
          </Button>
        </div>
        <pre className="overflow-x-auto px-3 py-2 font-mono text-[11px] leading-snug">
          <code>{snippet}</code>
        </pre>
      </div>
    </Card>
  );
}
